import { GermanyFederalState, PeakWindowType, quarterMaskOptions, Season } from '@e2m-customer-portal/domains';
import { useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import {
  DateTimePicker,
  formatRange,
  InfoTooltip,
  NumberInput,
  TextInput,
  TimeInput,
  ValidatedContainer,
} from '@e2m-customer-portal/ui';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { format } from 'date-fns';
import { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PeakWindowProps {
  data: PeakWindowType;
  peakWindows: PeakWindowType[];
  setPeakWindows: Dispatch<SetStateAction<PeakWindowType[] | undefined>>;
  setSaveBarOpen: Dispatch<SetStateAction<boolean>>;
  readOnly: (parameter?: string) => boolean;
  setIsValid: Dispatch<SetStateAction<boolean>>;
}

function PeakWindowMemo({
  data,
  peakWindows,
  setPeakWindows,
  setSaveBarOpen,
  readOnly,
  setIsValid,
}: Readonly<PeakWindowProps>) {
  const [t] = useTranslation();
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();

  const [updatedPeakWindow, setUpdatedPeakWindow] = useState<PeakWindowType>(data);

  useEffect(() => {
    const peak_windows = peakWindows.map((item) =>
      item.identifier === updatedPeakWindow.identifier ? updatedPeakWindow : item,
    );
    peak_windows && setPeakWindows(peak_windows);
  }, [updatedPeakWindow]);

  useEffect(() => {
    setUpdatedPeakWindow(data);
  }, [data]);

  return (
    <div className="relative px-4 py-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-grey-50 rounded-md border-solid border border-divider transition duration-300 ease-in-out">
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        <Button
          color="error"
          className="absolute -right-4 -top-4 bg-white h-8 w-8 min-w-8 shrink-0 grow-0 items-center justify-center rounded-full hover:bg-error-500 hover:text-white"
          variant="outlined"
          disabled={readOnly('peak_windows')}
          onClick={() => {
            setSaveBarOpen(true);
            setPeakWindows([...peakWindows.filter((item) => item.identifier !== updatedPeakWindow.identifier)]);
          }}
        >
          <span className="-translate-y-0.5">x</span>
        </Button>
        <FormControl className="w-full bg-white xl:col-span-2">
          <InputLabel id="seasons">{t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_NAME')}</InputLabel>
          <Select
            id="seasons-select"
            value={updatedPeakWindow.season.name}
            disabled={readOnly('peak_windows')}
            label={t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_NAME')}
            onChange={(e) => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({
                ...updatedPeakWindow,
                season: { ...updatedPeakWindow.season, name: e.target.value as Season },
              });
            }}
            endAdornment={
              <InfoTooltip
                iconClass="pr-4 w-4 sm:text-green-200"
                field="season_name"
                objectType="batteries_peak_windows"
              />
            }
          >
            {Object.entries(Season).map((element) => (
              <MenuItem value={element[0]} key={element[0]}>
                {element[1]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <InfoTooltip componentClass="p-0" field="start_date" objectType="batteries_peak_windows_season">
          <DateTimePicker
            disabled={readOnly('peak_windows')}
            label={t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_START_DATE')}
            mode={'single'}
            formats={{
              range: {
                patterns: { connector: '-', date: 'yyyy-MM-dd', time: '' },
                formatter: formatRange,
              },
            }}
            pickerProps={{
              disabled: readOnly('peak_windows'),
              numberOfMonths: 1,
              startOfWeek,
              showWeekNumber: true,
              dateOnly: true,
              locale,
            }}
            small={true}
            value={new Date(updatedPeakWindow.season.start_date)}
            onSelect={({ range: date }) => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({
                ...updatedPeakWindow,
                season: { ...updatedPeakWindow.season, start_date: format(date.start, 'yyyy-MM-dd') },
              });
            }}
          />
        </InfoTooltip>
        <InfoTooltip componentClass="p-0" field="end_date" objectType="batteries_peak_windows_season">
          <DateTimePicker
            disabled={readOnly('peak_windows')}
            label={t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_END_DATE')}
            mode={'single'}
            formats={{
              range: {
                patterns: { connector: '-', date: 'yyyy-MM-dd', time: '' },
                formatter: formatRange,
              },
            }}
            pickerProps={{
              disabled: readOnly('peak_windows'),
              numberOfMonths: 1,
              startOfWeek,
              showWeekNumber: true,
              dateOnly: true,
              locale,
            }}
            small={true}
            value={new Date(updatedPeakWindow.season.end_date)}
            onSelect={({ range: date }) => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({
                ...updatedPeakWindow,
                season: { ...updatedPeakWindow.season, end_date: format(date.start, 'yyyy-MM-dd') },
              });
            }}
          />
        </InfoTooltip>
        <TimeInput
          maskOptions={quarterMaskOptions}
          value={updatedPeakWindow.start_time_str}
          name="start_time_str"
          className="w-full p-2 bg-white"
          label={t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_START_TIME_STR')}
          disabled={readOnly('peak_windows')}
          required
          placeholder="HH:mm"
          onChange={(e, value: string) => {
            setSaveBarOpen(true);
            setUpdatedPeakWindow({ ...updatedPeakWindow, start_time_str: value });
          }}
          endAdornment={<InfoTooltip field="start_time_str" objectType="batteries_peak_windows_season" />}
        />
        <TimeInput
          maskOptions={quarterMaskOptions}
          value={updatedPeakWindow.end_time_str}
          name="end_time_str"
          className="w-full p-2 bg-white"
          label={t('COPT_BATTERIES_PEAK_WINDOWS_SEASON_END_TIME_STR')}
          disabled={readOnly('peak_windows')}
          placeholder="HH:mm"
          required
          onChange={(e, value: string) => {
            setSaveBarOpen(true);
            setUpdatedPeakWindow({ ...updatedPeakWindow, end_time_str: value });
          }}
          endAdornment={<InfoTooltip field="end_time_str" objectType="batteries_peak_windows_season" />}
        />
        <NumberInput
          value={updatedPeakWindow.start_soc !== null ? updatedPeakWindow.start_soc * 1000 : null}
          className="w-full bg-white"
          name="start_soc"
          label={t('COPT_BATTERIES_PEAK_WINDOWS_START_SOC')}
          required
          onChange={(value: number) => {
            setSaveBarOpen(true);
            setUpdatedPeakWindow({
              ...updatedPeakWindow,
              start_soc: value != null && !Number.isNaN(value) ? value / 1000 : null,
            });
          }}
          disabled={readOnly('peak_windows')}
          notNegative
          endAdornment={<InfoTooltip field="start_soc" objectType="batteries_peak_windows" />}
        />
        <NumberInput
          value={updatedPeakWindow.end_soc !== null ? updatedPeakWindow.end_soc * 1000 : null}
          className="w-full bg-white"
          name="end_soc"
          label={t('COPT_BATTERIES_PEAK_WINDOWS_END_SOC')}
          required
          onChange={(value: number) => {
            setSaveBarOpen(true);
            setUpdatedPeakWindow({
              ...updatedPeakWindow,
              end_soc: value != null && !Number.isNaN(value) ? value / 1000 : null,
            });
          }}
          disabled={readOnly('peak_windows')}
          notNegative
          endAdornment={<InfoTooltip field="end_soc" objectType="batteries_peak_windows" />}
        />
        <label className="flex items-center text-sm font-light mr-4">
          <input
            type="checkbox"
            name="exclude_weekends"
            checked={updatedPeakWindow.exclude_weekends}
            disabled={readOnly('peak_windows')}
            onChange={() => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({ ...updatedPeakWindow, exclude_weekends: !updatedPeakWindow.exclude_weekends });
            }}
            className="w-4 h-4 mr-2 accent-green-200"
          />
          {t('COPT_BATTERIES_PEAK_WINDOWS_EXCLUDE_WEEKENDS')}
          <InfoTooltip field="exclude_weekends" objectType="batteries_peak_windows" />
        </label>
        <label className="flex items-center text-sm font-light mr-4">
          <input
            type="checkbox"
            checked={updatedPeakWindow.exclude_last_days_of_the_year}
            disabled={readOnly('peak_windows')}
            onChange={() => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({
                ...updatedPeakWindow,
                exclude_last_days_of_the_year: !updatedPeakWindow.exclude_last_days_of_the_year,
              });
            }}
            className="w-4 h-4 mr-2 accent-green-200"
          />
          {t('COPT_BATTERIES_PEAK_WINDOWS_EXCLUDE_LAST_DAYS_OF_THE_YEAR')}
          <InfoTooltip field="exclude_last_days_of_the_year" objectType="batteries_peak_windows" />
        </label>
        <FormControl className="w-full bg-white">
          <InputLabel id="federal_state_holidays">{t('COPT_BATTERIES_PEAK_WINDOWS_FEDERAL_STATE_HOLIDAYS')}</InputLabel>
          <Select
            id="federal-state-select"
            value={updatedPeakWindow.federal_state_holidays ?? ''}
            disabled={readOnly('peak_windows')}
            label={t('COPT_BATTERIES_PEAK_WINDOWS_FEDERAL_STATE_HOLIDAYS')}
            onChange={(e) => {
              setSaveBarOpen(true);
              setUpdatedPeakWindow({
                ...updatedPeakWindow,
                federal_state_holidays: e.target.value === '' ? null : (e.target.value as GermanyFederalState),
              });
            }}
            endAdornment={
              <InfoTooltip
                field="federal_state_holidays"
                objectType="batteries_peak_windows"
                iconClass="pr-4 w-4 sm:text-green-200"
              />
            }
          >
            {Object.entries(GermanyFederalState).map((element) => (
              <MenuItem value={element[0]} key={element[0]}>
                {element[1]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextInput
          // This component will be a multiple date picker
          name="additional_exclude_dates"
          className="w-full bg-white"
          value={updatedPeakWindow.additional_exclude_dates?.join(', ')}
          disabled={readOnly('peak_windows')}
          label={t('COPT_BATTERIES_PEAK_WINDOWS_ADDITIONAL_EXCLUDE_DATES')}
          onChange={(value: string) => {
            setSaveBarOpen(true);
            const trimmedValue = value === '' ? [] : value.split(',').map((item) => item.trim());
            setUpdatedPeakWindow({
              ...updatedPeakWindow,
              additional_exclude_dates: trimmedValue,
            });
          }}
          endAdornment={<InfoTooltip field="additional_exclude_dates" objectType="batteries_peak_windows" />}
        />
      </ValidatedContainer>
    </div>
  );
}
export const PeakWindow = memo(PeakWindowMemo);
